// axios-offline.js
// This file configures axios to work in offline mode with SQLite database

import axios from "axios";
import store from "@/store";
// Note: offlineAuthService is only used for JSON fallback in non-Electron environments
// In Electron mode, authentication goes through the Express server at localhost:8080

// Check if running in Electron (offline mode)
const isElectron = window && window.electron;

// Debug logging
console.log("Axios-offline.js Debug:");
console.log("- window.electron exists:", !!window.electron);
console.log("- isElectron:", isElectron);
console.log("- window.location.href:", window.location.href);

// ===== LAN SYNC: Dynamic Base URL Configuration =====
// Check for Master IP configuration (for Client mode)
const getMasterIP = () => {
  if (!isElectron) return null;
  return localStorage.getItem("master_ip");
};

const getAppMode = () => {
  if (!isElectron) return "online";
  return localStorage.getItem("app_mode") || "master";
};

// Get stored port or default
const getStoredPort = () => {
  return localStorage.getItem("express_port") || "8080";
};

// Get master port (used when in client mode connecting to master)
const getMasterPort = () => {
  return localStorage.getItem("master_port") || "8080";
};

const masterIP = getMasterIP();
const appMode = getAppMode();
let expressPort = getStoredPort();
let masterPort = getMasterPort();

// Set base URL based on environment and mode
let baseURL;
if (!isElectron) {
  // Web browser - always online
  baseURL = "https://api.sns.aaibuzz.com";
  // baseURL = "http://sns-api.test";
} else if (appMode === "client" && masterIP) {
  // Client mode - connect to Master system over LAN (use master's port)
  baseURL = `http://${masterIP}:${masterPort}`;
  console.log(`[LAN SYNC] Client mode: Connecting to Master at ${masterIP}:${masterPort}`);
} else {
  // Master mode - use local server
  baseURL = `http://localhost:${expressPort}`;
  console.log(`[LAN SYNC] Master mode: Using local server on port ${expressPort}`);
}

console.log("- App Mode:", appMode);
console.log("- Master IP:", masterIP || "N/A");
console.log("- Master Port:", masterPort);
console.log("- Express Port:", expressPort);
console.log("- Calculated baseURL:", baseURL);
console.log("- Final axios baseURL will be:", baseURL + "/api");

// Configure axios defaults
if (isElectron) {
  // In offline mode, we'll intercept API calls and serve data from SQLite
  axios.defaults.baseURL = baseURL + "/api";
  console.log("Offline mode: Configured for SQLite database serving");
} else {
  // Online mode - use live API
  axios.defaults.baseURL = baseURL + "/api";
  console.log("Online mode: Configured for live API");
}

// Function to update baseURL with correct port (called after port detection)
export async function initializeExpressConnection() {
  if (!isElectron) return;

  try {
    // Get the actual LOCAL port from Electron main process
    const port = await window.electron.getExpressPort();
    if (port && port !== parseInt(expressPort)) {
      expressPort = port.toString();
      localStorage.setItem("express_port", expressPort);

      // For MASTER mode: update baseURL to use local express port
      // For CLIENT mode: keep baseURL pointing to master (using master_port), don't change it
      if (appMode === "client" && masterIP) {
        // Client mode - API baseURL stays as master's address (already set at startup)
        console.log(`[AXIOS] Client mode: Local express on port ${port}, API stays at master ${masterIP}:${masterPort}`);
      } else {
        // Master mode - update baseURL to use detected local port
        const newBaseURL = `http://localhost:${expressPort}`;
        axios.defaults.baseURL = newBaseURL + "/api";
        baseURL = newBaseURL;
        console.log(`[AXIOS] Master mode: Updated baseURL to port ${expressPort}: ${axios.defaults.baseURL}`);
        store.dispatch("settings/setBaseUrl", newBaseURL);
      }

      // Always update media URL - use LOCAL Express server for assets
      const newMediaURL = `http://localhost:${port}/data/assets/`;
      store.dispatch("settings/setMediaUrl", newMediaURL);
      console.log(`[AXIOS] Updated mediaURL: ${newMediaURL}`);
    }
  } catch (e) {
    console.warn("[AXIOS] Could not get Express port from IPC:", e);
  }
}

// Auto-initialize on load
if (isElectron) {
  // Small delay to ensure Electron IPC is ready
  setTimeout(() => {
    initializeExpressConnection();
  }, 100);
}

store.dispatch("settings/setBaseUrl", baseURL);

// Set media URL - for Electron, always use LOCAL Express server for assets
// Both Master and Client have local assets, so always use localhost
const mediaURL = isElectron
  ? `http://localhost:${expressPort}/data/assets/`
  : "https://blr1.vultrobjects.com/space-1/";

store.dispatch("settings/setMediaUrl", mediaURL);
console.log("- Media URL:", mediaURL);

// Request interceptor - Handle offline API calls
axios.interceptors.request.use(
  async (config) => {
    // Clear errors
    store.dispatch("errors/setErrors", {});
    store.dispatch("errors/setStatus", {});

    // If in online mode, let requests pass through without interception
    if (!isElectron) {
      return config;
    }

    // If in offline mode, intercept authentication requests
    if (isElectron) {
      config.headers["X-Offline-Mode"] = "true";

      // In Electron mode, route login requests to Express server
      if (config.url === "/login" && config.method === "post") {
        console.log("[AXIOS] POST /login - routing to Express server");
        config.url = `${baseURL}/api/login`;
        config.baseURL = "";
        return config;
      }

      // In Electron mode, route /me requests to Express server
      else if (config.url === "/me" && config.method === "get") {
        console.log("[AXIOS] GET /me - routing to Express server");
        config.url = `${baseURL}/api/me`;
        config.baseURL = "";
        return config;
      }

      // In Electron mode, route /course_candidates requests to Express server
      // This ensures proper SQL joins and filtering for candidate course listings
      else if (
        config.method === "get" &&
        (config.url.startsWith("/course_candidates") ||
          config.url.startsWith("course_candidates"))
      ) {
        console.log("[AXIOS] GET /course_candidates - routing to Express server");
        // Preserve the query string
        const urlPath = config.url.startsWith("/")
          ? config.url
          : "/" + config.url;
        config.url = `${baseURL}/api${urlPath}`;
        config.baseURL = "";
        return config;
      }

      // Route server-info to Express server (for network settings page)
      else if (config.url === "/server-info" && config.method === "get") {
        console.log("[AXIOS] GET /server-info - routing to Express server");
        config.url = `${baseURL}/api/server-info`;
        config.baseURL = "";
        return config;
      }

      // Route /users/:id requests to Express server (for profile pages)
      else if (
        config.method === "get" &&
        config.url.match(/^\/?users\/\d+$/)
      ) {
        console.log("[AXIOS] GET /users/:id - routing to Express server");
        const urlPath = config.url.startsWith("/") ? config.url : "/" + config.url;
        config.url = `${baseURL}/api${urlPath}`;
        config.baseURL = "";
        return config;
      }

      // Note: /users/masters is handled by local SQLite interception below
      // which correctly uses value_id to fetch ranks, vessels, etc.

      // In client mode, route /course/:id to master's Express server
      // This ensures course_candidates data comes from master's database
      else if (
        appMode === "client" &&
        config.method === "get" &&
        (config.url.match(/^\/course\/\d+/) || config.url.match(/^course\/\d+/))
      ) {
        console.log("[AXIOS] Client mode: GET /course/:id - routing to master Express server");
        const urlPath = config.url.startsWith("/") ? config.url : "/" + config.url;
        config.url = `${baseURL}/api${urlPath}`;
        config.baseURL = "";
        return config;
      }

      // Route sync endpoints to Express server
      else if (config.url.startsWith("/sync/")) {
        console.log("[AXIOS] Sync endpoint - routing to Express server:", config.url);
        config.url = `${baseURL}/api${config.url}`;
        config.baseURL = "";
        return config;
      }

      // Route Excel export endpoints to the Express server. These generate a real
      // .xlsx file and return its URL, so they must NOT be served from the generic
      // SQLite GET handler below (which would short-circuit with empty data).
      else if (
        config.method === "get" &&
        /^\/?(users|questions)Exports(\?|$)/.test(config.url)
      ) {
        console.log("[AXIOS] Excel export - routing to Express server:", config.url);
        const urlPath = config.url.startsWith("/") ? config.url : "/" + config.url;
        config.url = `${baseURL}/api${urlPath}`;
        config.baseURL = "";
        return config;
      }

      // CLIENT MODE: Route exam-related POST requests to master's Express server
      // This ensures all exam data (submissions, view counts, attempts) is stored on master
      else if (appMode === "client" && config.method === "post") {
        const examEndpoints = ["course_exams", "course_view_count", "course_candidates"];
        const isExamEndpoint = examEndpoints.some(ep => config.url.includes(ep));

        if (isExamEndpoint) {
          console.log(`[AXIOS] Client mode: POST ${config.url} - routing to master Express server`);
          const urlPath = config.url.startsWith("/") ? config.url : "/" + config.url;
          config.url = `${baseURL}/api${urlPath}`;
          config.baseURL = "";
          return config;
        }
      }

      // Intercept other API requests and serve from SQLite database
      else if (
        config.method === "get" &&
        !config.url.startsWith("http") &&
        window.electron.query
      ) {
        try {
          let fakeResponse = null;

          // Handle users/masters endpoint
          if (
            config.url === "users/masters" ||
            config.url === "/users/masters"
          ) {
            try {
              // Query all required master data from SQLite database
              const [
                usersResult,
                companiesResult,
                ranksResult,
                durationResult,
                vesselsResult,
                nationalityResult,
                birthPlaceResult,
                coursesResult,
                subjectsResult,
              ] = await Promise.all([
                window.electron
                  .query("SELECT * FROM users", [])
                  .catch(() => []),
                window.electron.query("SELECT * FROM orgs", []).catch(() => []),
                window.electron
                  .query("SELECT * FROM value_lists WHERE value_id = 1", [])
                  .catch(() => []), // RANK
                window.electron
                  .query("SELECT * FROM value_lists WHERE value_id = 2", [])
                  .catch(() => []), // DURATION
                window.electron
                  .query("SELECT * FROM value_lists WHERE value_id = 6", [])
                  .catch(() => []), // VESSELS
                window.electron
                  .query("SELECT * FROM value_lists WHERE value_id = 4", [])
                  .catch(() => []), // NATIONALITY
                window.electron
                  .query("SELECT * FROM value_lists WHERE value_id = 5", [])
                  .catch(() => []), // BIRTH PLACES
                window.electron
                  .query(`SELECT * FROM courses
                    WHERE (json_extract(data, '$.is_deleted') IS NULL OR json_extract(data, '$.is_deleted') != 1)
                    AND (json_extract(data, '$.is_active') != 0)
                    ORDER BY name ASC`, [])
                  .catch(() => []), // COURSES - only active
                window.electron
                  .query("SELECT * FROM value_lists WHERE value_id = 3", [])
                  .catch(() => []), // SUBJECTS
              ]);

              // Parse JSON data for each result
              const users = usersResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );
              const companies = companiesResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );
              const ranks = ranksResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );
              const duration = durationResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );
              const vessels = vesselsResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );
              const nationality = nationalityResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );
              const birthPlace = birthPlaceResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );
              const courses = coursesResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );
              const subjects = subjectsResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );

              // Structure the response to match what the frontend expects
              fakeResponse = {
                data: {
                  users: users,
                  companies: companies,
                  ranks: ranks,
                  duration: duration,
                  vessels: vessels,
                  nationality: nationality,
                  birth_place: birthPlace,
                  courses: courses,
                  subjects: subjects,
                  created_by: users, // For created_by dropdown
                  teachers: users.filter(
                    (user) =>
                      user.roles &&
                      user.roles.some(
                        (role) =>
                          role.name === "TEACHER" ||
                          role.name === "ADMIN" ||
                          role.name === "SUPER ADMIN"
                      )
                  ), // Filter users who are teachers/admins
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };

              console.log(
                "Offline mode: Loaded masters data from SQLite database"
              );
            } catch (error) {
              console.warn(
                "Offline mode: Failed to load masters data from SQLite:",
                error
              );
              // Fallback response with correct structure
              fakeResponse = {
                data: {
                  users: [],
                  companies: [],
                  ranks: [],
                  duration: [],
                  vessels: [],
                  nationality: [],
                  birth_place: [],
                  courses: [],
                  subjects: [],
                  created_by: [],
                  teachers: [],
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };
            }
          }

          // Handle value_lists/masters endpoint
          else if (
            config.url === "value_lists/masters" ||
            config.url === "/value_lists/masters"
          ) {
            try {
              // Query value lists masters data from SQLite database
              const valueListsResult = await window.electron.query(
                "SELECT * FROM value_lists",
                []
              );

              const valueListsData = valueListsResult.map((row) =>
                row.data ? JSON.parse(row.data) : row
              );

              fakeResponse = {
                data: valueListsData,
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };
            } catch (error) {
              console.warn(
                "Offline mode: Failed to load value lists masters data:",
                error
              );
              fakeResponse = {
                data: [],
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };
            }
          }

          // Handle value_lists/showAll endpoint
          else if (
            config.url === "value_lists/showAll" ||
            config.url === "/value_lists/showAll"
          ) {
            try {
              // Query all active value lists from SQLite database
              const valueListsResult = await window.electron.query(
                "SELECT * FROM value_lists WHERE is_active = 1",
                []
              );

              console.log(`📋 Offline mode: value_lists/showAll returned ${valueListsResult.length} items`);

              fakeResponse = {
                data: {
                  data: valueListsResult, // Note: nested data structure to match API
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };
            } catch (error) {
              console.error(
                "❌ Failed to handle value_lists/showAll endpoint:",
                error
              );
              fakeResponse = {
                data: { data: [] },
                status: 500,
                statusText: "Internal Server Error",
                headers: {},
                config: config,
              };
            }
          }

          // Handle admin_dashboard endpoint
          else if (config.url.includes("admin_dashboard")) {
            // For dashboard, we'll use mock data for now
            // In a real implementation, this would query aggregated data from the database
            fakeResponse = {
              data: {
                totalUser: 2,
                totalTeacher: 0,
                totalCourse: 1,
                totalCourseCompletedCandidate: 0,
                totaluser_present_today: 1,
                totaluser_absent_today: 1,
                totaluser_onleave_today: 0,
              },
              status: 200,
              statusText: "OK",
              headers: {},
              config: config,
            };
          }

          // Handle count endpoint
          else if (config.url === "count" || config.url === "/count") {
            try {
              // Query counts from SQLite database
              const [
                usersCountResult,
                coursesCountResult,
                questionsCountResult,
                companiesCountResult,
              ] = await Promise.all([
                window.electron
                  .query("SELECT COUNT(*) as count FROM users", [])
                  .catch(() => [{ count: 0 }]),
                window.electron
                  .query("SELECT COUNT(*) as count FROM courses", [])
                  .catch(() => [{ count: 0 }]),
                window.electron
                  .query("SELECT COUNT(*) as count FROM questions", [])
                  .catch(() => [{ count: 0 }]),
                window.electron
                  .query("SELECT COUNT(*) as count FROM companies", [])
                  .catch(() => [{ count: 0 }]),
              ]);

              fakeResponse = {
                data: {
                  users: usersCountResult[0]?.count || 0,
                  courses: coursesCountResult[0]?.count || 0,
                  questions: questionsCountResult[0]?.count || 0,
                  companies: companiesCountResult[0]?.count || 0,
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };
            } catch (error) {
              console.warn("Offline mode: Failed to get counts:", error);
              fakeResponse = {
                data: {
                  users: 0,
                  courses: 0,
                  questions: 0,
                  companies: 0,
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };
            }
          }

          // Handle truncate endpoints (just return success)
          else if (config.url.includes("truncate_")) {
            fakeResponse = {
              data: { message: "Data truncated successfully (offline mode)" },
              status: 200,
              statusText: "OK",
              headers: {},
              config: config,
            };
          }

          // Handle individual course endpoint (GET /course/{id} with optional query parameters)
          // In client mode, route to master's Express server instead of local SQLite
          else if (
            (config.url.match(/^\/course\/\d+(\?.*)?$/) ||
            config.url.match(/^course\/\d+(\?.*)?$/)) &&
            appMode !== "client"
          ) {
            const urlParts = config.url.split("?");
            const courseId = urlParts[0].split("/").pop();

            // Parse query parameters
            const urlParams = new URLSearchParams(urlParts[1] || "");
            const candidateId = urlParams.get("candidate_id");

            console.log(
              `🔍 Course endpoint: courseId=${courseId}, candidateId=${candidateId}`
            );

            try {
              // Query SQLite database for course
              const coursesResult = await window.electron.query(
                "SELECT * FROM courses WHERE id = ?",
                [courseId]
              );

              if (coursesResult && coursesResult.length > 0) {
                let course = coursesResult[0].data
                  ? JSON.parse(coursesResult[0].data)
                  : coursesResult[0];

                // If candidate_id is provided, add course_candidates data (matching Laravel API behavior)
                if (candidateId) {
                  console.log(
                    `📊 Fetching course_candidates for candidate ${candidateId} and course ${courseId}`
                  );
                  try {
                    const courseCandidatesResult = await window.electron.query(
                      "SELECT * FROM course_candidates WHERE candidate_id = ? AND course_id = ?",
                      [candidateId, courseId]
                    );

                    console.log(
                      `📋 Found ${
                        courseCandidatesResult
                          ? courseCandidatesResult.length
                          : 0
                      } course assignments`
                    );

                    // Add course_candidates array to the course data (matching Laravel structure)
                    course.course_candidates = courseCandidatesResult || [];
                  } catch (candidateError) {
                    console.error(
                      `❌ Error fetching course_candidates:`,
                      candidateError
                    );
                    course.course_candidates = [];
                  }
                }

                console.log(
                  `✅ Offline mode: Serving course ${courseId} from SQLite database with ${
                    course.course_candidates
                      ? course.course_candidates.length
                      : 0
                  } assignments`
                );

                fakeResponse = {
                  data: {
                    data: course,
                    success: true,
                  },
                  status: 200,
                  statusText: "OK",
                  headers: {},
                  config: config,
                };
              } else {
                console.warn(
                  `❌ Course ${courseId} not found in SQLite database`
                );
                fakeResponse = {
                  data: {
                    message: "Course not found",
                    success: false,
                  },
                  status: 404,
                  statusText: "Not Found",
                  headers: {},
                  config: config,
                };
              }
            } catch (error) {
              console.error(
                "❌ Offline mode: Failed to get course from database:",
                error
              );
              fakeResponse = {
                data: {
                  message: "Course not found",
                  success: false,
                },
                status: 404,
                statusText: "Not Found",
                headers: {},
                config: config,
              };
            }
          }

          // Handle users endpoint (candidate listing)
          else if (
            config.url.includes("users") &&
            !config.url.includes("masters") &&
            !config.url.includes("Exports") &&
            window.electron.query
          ) {
            try {
              // Handle individual user endpoint (GET /users/{id})
              const userMatch = config.url.match(/^\/?users\/(\d+)$/);
              if (userMatch) {
                const userId = userMatch[1];
                console.log(
                  "Offline mode: Handling individual user request for ID:",
                  userId
                );

                // 🔥 PRIORITY FIX: Try SQLite database FIRST (authoritative data source)
                try {
                  // Query SQLite database for user (prioritize database columns over JSON data)
                  const usersResult = await window.electron.query(
                    "SELECT * FROM users WHERE id = ? AND is_deleted = 0",
                    [userId]
                  );

                  if (usersResult && usersResult.length > 0) {
                    const userRow = usersResult[0];

                    // Parse JSON data as fallback for additional fields
                    let userData = {};
                    try {
                      userData = userRow.data ? JSON.parse(userRow.data) : {};
                    } catch (e) {
                      console.warn(
                        "Offline mode: Failed to parse user JSON data:",
                        e
                      );
                      userData = {};
                    }

                    // Query course assignments for this user
                    let courseCandidatesResult = [];
                    try {
                      courseCandidatesResult = await window.electron.query(
                        "SELECT * FROM course_candidates WHERE candidate_id = ?",
                        [userId]
                      );
                    } catch (queryError) {
                      console.error(
                        `Course candidates query failed for user ${userId}:`,
                        queryError
                      );
                      courseCandidatesResult = [];
                    }

                    // Query user's organization
                    const orgResult = await window.electron
                      .query("SELECT * FROM orgs WHERE id = ?", [
                        userRow.org_id,
                      ])
                      .catch(() => []);

                    // Build user data prioritizing database columns over JSON data
                    const user = {
                      // Core database columns take priority
                      id: userRow.id,
                      name: userRow.name || userData.name,
                      email: userRow.email || userData.email,
                      phone: userRow.phone || userData.phone,
                      rank: userRow.rank || userData.rank,
                      active: Boolean(userRow.active),
                      is_approved: Boolean(userRow.is_approved),
                      org_id: userRow.org_id,
                      assign_teacher_id: userRow.assign_teacher_id,
                      role_id: userRow.role_id,

                      // Additional fields from database columns or JSON fallback
                      last_name: userRow.last_name || userData.last_name,
                      middle_name: userRow.middle_name || userData.middle_name,
                      gender: userRow.gender || userData.gender,
                      dob: userRow.dob || userData.dob,
                      nationality: userRow.nationality || userData.nationality,
                      place_of_birth:
                        userRow.place_of_birth || userData.place_of_birth,
                      passport_no: userRow.passport_no || userData.passport_no,
                      cdc: userRow.cdc || userData.cdc,
                      coc: userRow.coc || userData.coc,
                      sid: userRow.sid || userData.sid,
                      indos: userRow.indos || userData.indos,
                      age: userRow.age || userData.age,
                      contract_expiry:
                        userRow.contract_expiry || userData.contract_expiry,
                      on_board_since:
                        userRow.on_board_since || userData.on_board_since,
                      vessel_id: userRow.vessel_id || userData.vessel_id,
                      whatsapp_no: userRow.whatsapp_no || userData.whatsapp_no,

                      // Image paths
                      image_path: userRow.image_path || userData.image_path,
                      passport_image_path:
                        userRow.passport_image_path ||
                        userData.passport_image_path,
                      cdc_image_path:
                        userRow.cdc_image_path || userData.cdc_image_path,
                      coc_image_path:
                        userRow.coc_image_path || userData.coc_image_path,
                      sid_image_path:
                        userRow.sid_image_path || userData.sid_image_path,
                      watch_keeping_image_path:
                        userRow.watch_keeping_image_path ||
                        userData.watch_keeping_image_path,
                      signature_image_path:
                        userRow.signature_image_path ||
                        userData.signature_image_path,
                      cop_image_path:
                        userRow.cop_image_path || userData.cop_image_path,

                      // Organization data
                      org: orgResult.length > 0 ? orgResult[0] : null,

                      // Create roles structure from database columns
                      roles:
                        userRow.role_id && userRow.role_name
                          ? [
                              {
                                id: userRow.role_id,
                                name: userRow.role_name,
                                pivot: {
                                  user_id: userRow.id,
                                  role_id: userRow.role_id,
                                },
                              },
                            ]
                          : userData.roles || [],

                      // Timestamps
                      created_at: userRow.created_at || userData.created_at,
                      updated_at: userRow.updated_at || userData.updated_at,

                      // Additional JSON data that doesn't have corresponding columns (but don't overwrite critical fields)
                      ...userData,

                      // 🔥 CRITICAL FIX: Add course assignments from SQLite database AFTER userData spread
                      // This ensures SQLite data takes priority over JSON data
                      course_candidates: courseCandidatesResult || [],
                    };

                    fakeResponse = {
                      data: {
                        data: user,
                        success: true,
                      },
                      status: 200,
                      statusText: "OK",
                      headers: {},
                      config: config,
                    };
                    console.log(
                      `Offline mode: Found user in SQLite database with ${courseCandidatesResult.length} course assignments:`,
                      userId
                    );
                  }
                } catch (dbError) {
                  console.warn(
                    "Offline mode: SQLite database failed, trying IndexedDB fallback:",
                    dbError
                  );
                }

                // 🔄 FALLBACK: Only if SQLite fails, try IndexedDB storage
                if (!fakeResponse) {
                  try {
                    const { offlineCandidateStorage } = await import(
                      "@/services/offline-candidate-storage"
                    );
                    const offlineCandidate =
                      await offlineCandidateStorage.getCandidateById(userId);

                    if (offlineCandidate) {
                      fakeResponse = {
                        data: {
                          data: offlineCandidate,
                          success: true,
                        },
                        status: 200,
                        statusText: "OK",
                        headers: {},
                        config: config,
                      };
                      console.log(
                        "Offline mode: Found user in IndexedDB storage (fallback):",
                        userId
                      );
                    }
                  } catch (storageError) {
                    console.warn(
                      "Offline mode: IndexedDB storage also failed:",
                      storageError
                    );
                  }
                }

                // 🚫 NOT FOUND: User not found in either storage
                if (!fakeResponse) {
                  fakeResponse = {
                    data: {
                      message: "User not found",
                      success: false,
                    },
                    status: 404,
                    statusText: "Not Found",
                    headers: {},
                    config: config,
                  };
                }
              }
              // Handle users listing endpoint
              else {
                try {
                  // Extract query parameters
                  const urlParts = config.url.split("?");
                  const urlParams = new URLSearchParams(urlParts[1] || "");
                  const page = parseInt(urlParams.get("page")) || 1;
                  const rowsPerPage =
                    parseInt(urlParams.get("rowsPerPage")) || 10;
                  const searchKeyword = urlParams.get("search_keyword") || "";
                  const assignedTeacherId =
                    urlParams.get("assigned_teacher_id") || "";
                  const isActive = urlParams.get("is_active") || "";
                  const roleId = urlParams.get("roleId") || "4"; // Default to candidates

                  // Build SQL query based on filters
                  let sql = "SELECT * FROM users";
                  let params = [];
                  let conditions = [];

                  // Filter by role ID
                  if (roleId) {
                    conditions.push("role_id = ?");
                    params.push(roleId);
                  }

                  // Apply search filter
                  if (searchKeyword) {
                    conditions.push(
                      "(name LIKE ? OR email LIKE ? OR phone LIKE ?)"
                    );
                    params.push(
                      `%${searchKeyword}%`,
                      `%${searchKeyword}%`,
                      `%${searchKeyword}%`
                    );
                  }

                  // Apply teacher filter
                  if (assignedTeacherId) {
                    conditions.push("assign_teacher_id = ?");
                    params.push(assignedTeacherId);
                  }

                  // Apply active status filter
                  if (isActive !== "") {
                    conditions.push("active = ?");
                    params.push(isActive);
                  }

                  // Add conditions to SQL
                  if (conditions.length > 0) {
                    sql += " WHERE " + conditions.join(" AND ");
                  }

                  // Add ordering
                  sql += " ORDER BY id";

                  // Query SQLite database for users
                  const usersResult = await window.electron.query(sql, params);

                  // Parse user data and ensure critical fields come from database columns
                  let users = usersResult.map((row) => {
                    let userData = row.data ? JSON.parse(row.data) : {};
                    return {
                      ...userData,
                      id: row.id,
                      active: Boolean(row.active),
                      is_approved: Boolean(row.is_approved),
                      name: row.name || userData.name,
                      email: row.email || userData.email,
                      rank: row.rank || userData.rank,
                      phone: row.phone || userData.phone,
                    };
                  });

                  // Apply pagination
                  const startIndex = (page - 1) * rowsPerPage;
                  const paginatedUsers = users.slice(
                    startIndex,
                    startIndex + rowsPerPage
                  );
                  const count = users.length;

                  fakeResponse = {
                    data: {
                      data: paginatedUsers,
                      count: count,
                      success: true,
                    },
                    status: 200,
                    statusText: "OK",
                    headers: {},
                    config: config,
                  };

                  console.log(
                    `Offline mode: Loaded ${paginatedUsers.length} users for page ${page} from SQLite database`
                  );
                } catch (dbError) {
                  console.warn(
                    "Offline mode: Failed to load users from SQLite database:",
                    dbError
                  );
                  fakeResponse = {
                    data: {
                      data: [],
                      count: 0,
                      success: true,
                    },
                    status: 200,
                    statusText: "OK",
                    headers: {},
                    config: config,
                  };
                }
              }
            } catch (error) {
              console.warn("Offline mode: Failed to load users data:", error);
            }
          }

          // Handle course listing endpoint (GET /course)
          // IMPORTANT: Exclude /course_candidates - it's handled separately below
          else if (
            (config.url.startsWith("/course") ||
            config.url.startsWith("course")) &&
            !config.url.includes("course_candidates")
          ) {
            // Check if it's the course listing endpoint (not individual course)
            const isCourseListing =
              config.url === "/course" ||
              config.url === "course" ||
              config.url.startsWith("/course?") ||
              config.url.startsWith("course?");

            if (isCourseListing && window.electron.query) {
              try {
                // Parse query parameters from URL
                let activeFilter = '1'; // Default to active courses
                let page = 1;
                let rowsPerPage = 10;
                let searchKeyword = '';
                let subjectId = '';
                let createdById = '';

                const urlParts = config.url.split('?');
                if (urlParts.length > 1) {
                  const params = new URLSearchParams(urlParts[1]);
                  activeFilter = params.get('active') || '1';
                  page = parseInt(params.get('page')) || 1;
                  rowsPerPage = parseInt(params.get('rowsPerPage')) || 10;
                  searchKeyword = params.get('search_keyword') || '';
                  subjectId = params.get('subject_id') || '';
                  createdById = params.get('created_by_id') || '';
                }
                // Also check config.params
                if (config.params) {
                  if (config.params.active !== undefined) activeFilter = config.params.active;
                  if (config.params.page !== undefined) page = parseInt(config.params.page) || 1;
                  if (config.params.rowsPerPage !== undefined) rowsPerPage = parseInt(config.params.rowsPerPage) || 10;
                  if (config.params.search_keyword !== undefined) searchKeyword = config.params.search_keyword;
                  if (config.params.subject_id !== undefined) subjectId = config.params.subject_id;
                  if (config.params.created_by_id !== undefined) createdById = config.params.created_by_id;
                }

                // Suppress unused variable warning - createdById reserved for future use
                void createdById;

                console.log(`[AXIOS-OFFLINE] Course listing - active: "${activeFilter}", page: ${page}, rowsPerPage: ${rowsPerPage}`);

                // Build SQL query with is_active filter using JSON data
                let sqlQuery = "SELECT * FROM courses WHERE (json_extract(data, '$.is_deleted') IS NULL OR json_extract(data, '$.is_deleted') != 1)";
                let countQuery = "SELECT COUNT(*) as total FROM courses WHERE (json_extract(data, '$.is_deleted') IS NULL OR json_extract(data, '$.is_deleted') != 1)";

                // Filter by active status
                if (activeFilter === 'all') {
                  console.log("[AXIOS-OFFLINE] Showing ALL courses");
                } else if (activeFilter === '0') {
                  sqlQuery += " AND json_extract(data, '$.is_active') = 0";
                  countQuery += " AND json_extract(data, '$.is_active') = 0";
                  console.log("[AXIOS-OFFLINE] Showing INACTIVE courses");
                } else {
                  sqlQuery += " AND (json_extract(data, '$.is_active') != 0)";
                  countQuery += " AND (json_extract(data, '$.is_active') != 0)";
                  console.log("[AXIOS-OFFLINE] Showing ACTIVE courses only");
                }

                // Filter by subject_id
                if (subjectId) {
                  sqlQuery += ` AND subject_id = ${parseInt(subjectId)}`;
                  countQuery += ` AND subject_id = ${parseInt(subjectId)}`;
                }

                // Filter by search keyword
                if (searchKeyword) {
                  const searchTerm = `%${searchKeyword}%`;
                  sqlQuery += ` AND (name LIKE '${searchTerm}' OR json_extract(data, '$.name') LIKE '${searchTerm}')`;
                  countQuery += ` AND (name LIKE '${searchTerm}' OR json_extract(data, '$.name') LIKE '${searchTerm}')`;
                }

                // Get total count first
                const countResult = await window.electron.query(countQuery, []);
                const totalCount = countResult[0] ? countResult[0].total : 0;

                // Add pagination
                sqlQuery += " ORDER BY id DESC";
                if (rowsPerPage > 0) {
                  const offset = (page - 1) * rowsPerPage;
                  sqlQuery += ` LIMIT ${rowsPerPage} OFFSET ${offset}`;
                }

                const coursesResult = await window.electron.query(sqlQuery, []);

                // Fetch subjects to enrich courses
                let subjects = [];
                try {
                  subjects = await window.electron.query("SELECT * FROM value_lists WHERE value_id = 3", []);
                } catch (e) {
                  console.warn("Failed to fetch subjects:", e);
                }

                const courses = coursesResult.map((row) => {
                  const course = row.data ? JSON.parse(row.data) : row;
                  // Ensure id is set from the row
                  course.id = row.id || course.id;
                  // Add subject relation if subject_id exists
                  if (course.subject_id && subjects.length > 0) {
                    const subjectRow = subjects.find(s => s.id === course.subject_id);
                    if (subjectRow) {
                      course.subject = subjectRow.data ? JSON.parse(subjectRow.data) : subjectRow;
                    }
                  }
                  return course;
                });

                fakeResponse = {
                  data: {
                    data: courses,
                    count: totalCount,
                  },
                  status: 200,
                  statusText: "OK",
                  headers: {},
                  config: config,
                };

                console.log(
                  `Offline mode: Loaded ${courses.length} of ${totalCount} courses from SQLite database (page ${page})`
                );
              } catch (error) {
                console.warn(
                  "Offline mode: Failed to load courses from SQLite database:",
                  error
                );
                fakeResponse = {
                  data: {
                    data: [],
                    count: 0,
                  },
                  status: 200,
                  statusText: "OK",
                  headers: {},
                  config: config,
                };
              }
            }

            // For individual course requests, let them fall through to the existing handling
          }

          // Handle course_exams GET requests (certificate requests)
          else if (
            (config.url === "/course_exams" || config.url === "course_exams") &&
            (config.params?.is_certificate === "1" ||
             config.params?.is_download === "true" ||
             config.params?.certificate_no)
          ) {
            console.log("📋 Certificate request detected - passing to Express server:", config.params);
            // Let certificate requests pass through to Express server for proper handling
            return Promise.resolve(config);
          }

          // Handle certificate viewing endpoints
          else if (config.url.startsWith("/certificates/") || config.url.startsWith("certificates/")) {
            console.log("📄 Certificate viewing request - passing to Express server");
            return Promise.resolve(config);
          }

          // Handle candidate certificate list requests
          else if (config.url.includes("/candidates/") && config.url.includes("/certificates")) {
            console.log("📋 Certificate list request - passing to Express server");
            return Promise.resolve(config);
          }

          // Handle other endpoints by querying SQLite database
          else if (window.electron.query) {
            // Try to determine the appropriate table based on the endpoint
            const endpoint = config.url.replace(/^\//, "");
            let tableName = null;

            // Map common endpoints to table names
            const endpointToTableMap = {
              course_questions: "questions",
              course_candidates: "course_candidates",
              course_exams: "course_exams",
              companies: "companies",
              subjects: "value_lists", // Assuming subjects are in value_lists
              values: "values",
              "value_lists/masters": "value_lists",
            };

            tableName = endpointToTableMap[endpoint] || null;

            // Special handling for course_candidates - include course_view_count relationships
            if (
              endpoint === "course_candidates" &&
              config.params &&
              config.params.candidate_id
            ) {
              try {
                const candidateId = config.params.candidate_id;

                // Get course_candidates with course and course_view_count data
                const courseCandidatesResult = await window.electron.query(
                  "SELECT * FROM course_candidates WHERE candidate_id = ?",
                  [candidateId]
                );

                // For each course_candidate, get the course and course_view_count data
                const enrichedData = await Promise.all(
                  courseCandidatesResult.map(async (candidate) => {
                    // Get course data
                    const courseResult = await window.electron.query(
                      "SELECT * FROM courses WHERE id = ?",
                      [candidate.course_id]
                    );

                    // Get course_view_count data
                    const viewCountResult = await window.electron.query(
                      "SELECT * FROM course_view_counts WHERE candidate_id = ? AND course_id = ?",
                      [candidateId, candidate.course_id]
                    );

                    // Parse course data from JSON if it exists
                    let courseData = null;
                    if (courseResult.length > 0) {
                      const courseRow = courseResult[0];
                      courseData = courseRow.data
                        ? JSON.parse(courseRow.data)
                        : courseRow;
                    }

                    return {
                      ...candidate,
                      course: courseData,
                      course_view_count:
                        viewCountResult.length > 0 ? viewCountResult[0] : null,
                      candidate: null, // This will be populated by the API if needed
                    };
                  })
                );

                fakeResponse = {
                  data: {
                    data: enrichedData,
                    count: enrichedData.length,
                  },
                  status: 200,
                  statusText: "OK",
                  headers: {},
                  config: config,
                };

                console.log(
                  `Offline mode: Loaded ${enrichedData.length} course_candidates with relationships`
                );
              } catch (error) {
                console.warn(
                  "Offline mode: Failed to load course_candidates with relationships:",
                  error
                );
                // Fallback to basic query
                fakeResponse = {
                  data: {
                    data: [],
                    count: 0,
                  },
                  status: 200,
                  statusText: "OK",
                  headers: {},
                  config: config,
                };
              }
            } else if (tableName) {
              try {
                // Query SQLite database for the appropriate table
                // Quote table name for SQL reserved keywords like "values"
                const quotedTable = tableName === 'values' ? '"values"' : tableName;
                let sql = `SELECT * FROM ${quotedTable}`;
                let params = [];

                // Add specific conditions for certain tables
                // Parse course_id from either config.params or URL query string
                let courseIdParam = config.params?.course_id;
                if (!courseIdParam && config.url.includes('course_id=')) {
                  const urlParams = new URLSearchParams(config.url.split('?')[1] || '');
                  courseIdParam = urlParams.get('course_id');
                }

                if (endpoint === "course_questions" && courseIdParam) {
                  sql = `SELECT cq.*, c.name, c.exam_duration, c.no_of_quiz, c.passing_marks
           FROM course_questions cq
           LEFT JOIN courses c ON cq.course_id = c.id
           WHERE cq.course_id = ? AND cq.is_deleted = 0`;
                  params.push(courseIdParam);
                } else if (endpoint === "course_questions") {
                  // No course_id but still filter deleted questions
                  sql = `SELECT * FROM course_questions WHERE is_deleted = 0`;
                } else if (
                  tableName === "value_lists" &&
                  endpoint === "subjects"
                ) {
                  sql += " WHERE value_id = 3"; // Assuming subject value_id is 3
                }

                const result = await window.electron.query(sql, params);

                // Special handling for course_questions with JOIN data
                const data =
                  endpoint === "course_questions"
                    ? result.map((row) => ({
                        // Question data
                        id: row.id,
                        course_id: row.course_id,
                        question: row.question,
                        option_1: row.option_1,
                        option_2: row.option_2,
                        option_3: row.option_3,
                        option_4: row.option_4,
                        correct_answer: row.correct_answer,
                        marks: row.marks,
                        is_active: row.is_active,
                        is_deleted: row.is_deleted,
                        created_at: row.created_at,
                        updated_at: row.updated_at,
                        // Course data (from JOIN)
                        course: {
                          id: row.course_id,
                          name: row.name,
                          exam_duration: row.exam_duration,
                          no_of_quiz: row.no_of_quiz,
                          passing_marks: row.passing_marks,
                        },
                      }))
                    : result.map((row) =>
                        row.data ? JSON.parse(row.data) : row
                      );

                fakeResponse = {
                  data: {
                    data: data,
                  },
                  status: 200,
                  statusText: "OK",
                  headers: {},
                  config: config,
                };

                console.log(
                  `Offline mode: Loaded ${data.length} records from ${tableName} table`
                );
              } catch (error) {
                console.warn(
                  `Offline mode: Failed to load data from ${tableName} table:`,
                  error
                );
                // Return empty data instead of falling back to JSON
                fakeResponse = {
                  data: {
                    data: [],
                  },
                  status: 200,
                  statusText: "OK",
                  headers: {},
                  config: config,
                };
              }
            }

            // If we couldn't handle it specifically, return empty data instead of falling back to JSON
            if (!fakeResponse) {
              console.warn(
                `Offline mode: Unhandled endpoint ${config.url}, returning empty data`
              );
              fakeResponse = {
                data: {
                  data: [],
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };
            }
          }

          if (fakeResponse) {
            config.adapter = () => Promise.resolve(fakeResponse);
          }
        } catch (error) {
          console.warn(
            `Offline mode: Could not load local data for ${config.url}`,
            error
          );
        }
      }

      // Handle POST requests (create/update operations)
      else if (config.method === "post" && !config.url.startsWith("http")) {
        // Debug: Log all POST requests to help identify URL patterns
        console.log(`[AXIOS DEBUG] POST request URL: "${config.url}"`);

        try {
          let fakeResponse = null;

          // Handle course_view_count tracking - Save to SQLite database
          // In client mode, route to master's Express server
          if (config.url.includes("course_view_count") && appMode !== "client") {
            try {
              const viewData = config.data || {};
              const candidateId = viewData.candidate_id;
              const courseId = viewData.course_id;
              const videoCount = viewData.video_count || 0;
              const documentCount = viewData.document_count || 0;
              const textCount = viewData.text_count || 0;

              console.log(
                `📊 Recording course view: candidate=${candidateId}, course=${courseId}, video=${videoCount}, doc=${documentCount}, text=${textCount}`
              );

              // Check if course_view_count record already exists
              const existingViewCount = await window.electron.query(
                "SELECT * FROM course_view_counts WHERE candidate_id = ? AND course_id = ?",
                [candidateId, courseId]
              );

              if (existingViewCount && existingViewCount.length > 0) {
                // Update existing record
                await window.electron.query(
                  `UPDATE course_view_counts SET
                   video_count = COALESCE(video_count, 0) + ?,
                   document_count = COALESCE(document_count, 0) + ?,
                   text_count = COALESCE(text_count, 0) + ?,
                   updated_at = datetime('now')
                   WHERE candidate_id = ? AND course_id = ?`,
                  [videoCount, documentCount, textCount, candidateId, courseId]
                );
                console.log(
                  `✅ Updated course view count for candidate ${candidateId}`
                );
              } else {
                // Create new record
                await window.electron.query(
                  `INSERT INTO course_view_counts
                   (candidate_id, course_id, video_count, document_count, text_count, is_active, is_deleted, created_at, updated_at)
                   VALUES (?, ?, ?, ?, ?, 1, 0, datetime('now'), datetime('now'))`,
                  [candidateId, courseId, videoCount, documentCount, textCount]
                );
                console.log(
                  `✅ Created new course view count record for candidate ${candidateId}`
                );
              }

              fakeResponse = {
                data: {
                  message: "View count updated successfully",
                  id: Date.now(),
                  candidate_id: candidateId,
                  course_id: courseId,
                  success: true,
                },
                status: 201,
                statusText: "Created",
                headers: {},
                config: config,
              };
            } catch (error) {
              console.error(
                "❌ Failed to save course view count to SQLite:",
                error
              );
              fakeResponse = {
                data: {
                  message: "View count update failed",
                  error: error.message,
                  success: false,
                },
                status: 500,
                statusText: "Internal Server Error",
                headers: {},
                config: config,
              };
            }
          }

          // Handle Reset Attempts (course_candidates/{id}/reset-attempts) AND the bulk
          // variant (course_candidates/bulk-reset-attempts, which also contains the
          // substring "reset-attempts" so it matches this same branch) - route to
          // Express server PRESERVING the full path. Must come BEFORE the generic
          // course_candidates branch below, which would otherwise strip the
          // /{id}/reset-attempts segment and hit the enrollment endpoint instead.
          else if (
            config.url.includes("course_candidates") &&
            config.url.includes("reset-attempts")
          ) {
            const urlPath = config.url.startsWith("/") ? config.url : `/${config.url}`;
            console.log("[AXIOS] POST course_candidates reset-attempts - routing to Express server");
            config.url = `${baseURL}/api${urlPath}`;
            config.baseURL = "";
            return config;
          }

          // Handle Mark as Completed (course_candidates/{id}/mark-complete) - route to
          // Express server PRESERVING the full path. Must come BEFORE the generic
          // course_candidates branch below (same reason as reset-attempts above).
          else if (
            config.url.includes("course_candidates") &&
            config.url.includes("mark-complete")
          ) {
            const urlPath = config.url.startsWith("/") ? config.url : `/${config.url}`;
            console.log("[AXIOS] POST course_candidates mark-complete - routing to Express server");
            config.url = `${baseURL}/api${urlPath}`;
            config.baseURL = "";
            return config;
          }

          // Handle course_candidates POST (course enrollment) - route to Express server
          else if (
            config.url.includes("course_candidates") &&
            !config.url.startsWith("http")
          ) {
            console.log("[AXIOS] POST /course_candidates - routing to Express server");
            config.url = `${baseURL}/api/course_candidates`;
            config.baseURL = "";
            return config;
          }

          // Handle course creation - route to Express server
          else if (config.url === "/course" || config.url === "course") {
            console.log("[AXIOS] POST /course - routing to Express server");
            config.url = `${baseURL}/api/course`;
            config.baseURL = "";
            return config;
          }

          // Handle video_and_document_upload - route to Express server for actual file saving
          else if (
            config.url === "video_and_document_upload" ||
            config.url === "/video_and_document_upload"
          ) {
            console.log("[AXIOS] POST /video_and_document_upload - routing to Express server for actual file upload");
            config.url = `${baseURL}/api/video_and_document_upload`;
            config.baseURL = "";
            return config;
          }

          // Handle user creation (candidates)
          // Route to Express server for proper database storage
          // Match various URL patterns: /users, users, /api/users, /users/, etc.
          else if (
            config.url === "/users" ||
            config.url === "users" ||
            config.url === "/users/" ||
            config.url === "/api/users" ||
            config.url === "api/users" ||
            config.url.match(/^\/?(api\/)?users\/?$/)
          ) {
            // Route to Express server which handles SQLite database operations
            console.log("[AXIOS] POST /users - routing to Express server");
            config.url = `${baseURL}/api/users`;
            config.baseURL = "";
            // Return early - don't set fakeResponse, let the request go to Express
            return config;
          }

          // Handle course_exams POST (exam submission)
          // Route to Express server which handles SQLite database operations and sync queue logging
          else if (
            config.url === "/course_exams" ||
            config.url === "course_exams"
          ) {
            console.log("[AXIOS] POST /course_exams - routing to Express server for sync queue support");
            config.url = `${baseURL}/api/course_exams`;
            config.baseURL = "";
            // Return early - don't set fakeResponse, let the request go to Express
            return config;
          }

          // Handle generic create operations - only in offline mode
          else {
            // Only intercept generic creates in offline mode
            if (isElectron) {
              const newId = Date.now(); // Generate a fake ID
              fakeResponse = {
                data: {
                  ...config.data,
                  id: newId,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  message: "Created successfully (offline mode)",
                },
                status: 201,
                statusText: "Created",
                headers: {},
                config: config,
              };
            }
            // In online mode, let POST requests pass through normally
          }

          if (fakeResponse) {
            config.adapter = () => Promise.resolve(fakeResponse);
            console.log(`Offline mode: Handled POST ${config.url}`);
          }
        } catch (error) {
          console.warn(
            `Offline mode: Could not handle POST for ${config.url}`,
            error
          );
        }
      }

      // Handle PUT/PATCH requests (update operations)
      else if (
        (config.method === "put" || config.method === "patch") &&
        !config.url.startsWith("http")
      ) {
        try {
          let fakeResponse = null;

          // Handle course update (PATCH /course/{id})
          if (
            config.url.match(/^\/course\/\d+$/) ||
            config.url.match(/^course\/\d+$/)
          ) {
            const courseId = config.url.split("/").pop();

            // Don't intercept - let it go to the local API server
            // The local API server at localhost:8080 will handle the actual database update
            console.log(
              `Offline mode: Allowing ${config.method.toUpperCase()} ${
                config.url
              } to reach local API server`
            );

            // Modify the URL to point to our API server (Master or local)
            config.url = `${baseURL}/api/course/${courseId}`;
            config.baseURL = "";

            // Return the config without intercepting
            return config;
          }
          // Handle user updates (candidates) - Route to API server in Electron mode
          else if (
            config.url.match(/^\/users\/\d+$/) ||
            config.url.match(/^users\/\d+$/)
          ) {
            const userId = config.url.split("/").pop();

            // Don't intercept - let it go to the API server
            // The API server will handle the actual database update
            console.log(
              `Offline mode: Allowing ${config.method.toUpperCase()} ${
                config.url
              } to reach API server at ${baseURL}`
            );

            // Modify the URL to point to our API server (Master or local)
            config.url = `${baseURL}/api/users/${userId}`;
            config.baseURL = "";

            // Return the config without intercepting
            return config;
          }
          // Handle generic updates - only in offline mode
          else {
            // Only intercept generic updates in offline mode
            if (isElectron) {
              fakeResponse = {
                data: {
                  ...config.data,
                  updated_at: new Date().toISOString(),
                  message: "Updated successfully (offline mode)",
                },
                status: 200,
                statusText: "OK",
                headers: {},
                config: config,
              };

              config.adapter = () => Promise.resolve(fakeResponse);
              console.log(
                `Offline mode: Handled ${config.method.toUpperCase()} ${
                  config.url
                }`
              );
            }
            // In online mode, let all other requests pass through normally
          }
        } catch (error) {
          console.warn(
            `Offline mode: Could not handle ${config.method.toUpperCase()} for ${
              config.url
            }`,
            error
          );
        }
      }

      // Handle DELETE requests - only in offline mode
      else if (
        config.method === "delete" &&
        !config.url.startsWith("http") &&
        isElectron
      ) {
        try {
          const fakeResponse = {
            data: { message: "Deleted successfully (offline mode)" },
            status: 200,
            statusText: "OK",
            headers: {},
            config: config,
          };

          config.adapter = () => Promise.resolve(fakeResponse);
          console.log(`Offline mode: Handled DELETE ${config.url}`);
        } catch (error) {
          console.warn(
            `Offline mode: Could not handle DELETE for ${config.url}`,
            error
          );
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axios.interceptors.response.use(
  async (response) => {
    // Only handle course and candidate update responses in offline mode
    if (isElectron && response.config) {
      const { url, method } = response.config;

      // Check if this is a course update response
      if (
        (method === "patch" || method === "put") &&
        url &&
        url.includes("/course/")
      ) {
        try {
          const courseIdMatch = url.match(/\/course\/(\d+)/);
          if (courseIdMatch && response.data && response.data.data) {
            const courseId = courseIdMatch[1];
            const updatedCourseData = response.data.data;

            // Store the updated course data in offline storage
            const { offlineCourseStorage } = await import(
              "@/services/offline-course-storage"
            );
            await offlineCourseStorage.storeUpdatedCourse(
              courseId,
              updatedCourseData
            );

            console.log(
              "Offline mode: Stored updated course data for ID:",
              courseId
            );
          }
        } catch (error) {
          console.warn(
            "Offline mode: Failed to store updated course data:",
            error
          );
        }
      }

      // Check if this is a candidate update response
      else if (
        (method === "patch" || method === "put") &&
        url &&
        url.includes("/users/")
      ) {
        try {
          const userIdMatch = url.match(/\/users\/(\d+)/);
          if (userIdMatch && response.data && response.data.data) {
            const userId = userIdMatch[1];
            const updatedCandidateData = response.data.data;

            // Store the updated candidate data in offline storage
            const { offlineCandidateStorage } = await import(
              "@/services/offline-candidate-storage"
            );
            await offlineCandidateStorage.storeUpdatedCandidate(
              userId,
              updatedCandidateData
            );

            console.log(
              "Offline mode: Stored updated candidate data for ID:",
              userId
            );
          }
        } catch (error) {
          console.warn(
            "Offline mode: Failed to store updated candidate data:",
            error
          );
        }
      }
    }

    return response;
  },
  (error) => {
    // Enhanced error handling
    console.error("Axios error:", error);

    if (error.response && error.response.data) {
      // Handle Laravel validation errors
      if (error.response.data.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          errors[key] = Array.isArray(errors[key])
            ? errors[key][0]
            : errors[key];
        });
        store.dispatch("errors/setErrors", errors);
      }

      // Set error status
      store.dispatch("errors/setStatus", error.response.status);

      // Log user-friendly error message
      const status = error.response.status;
      let message = "An error occurred";

      switch (status) {
        case 400:
          message = "Bad request - please check your input";
          break;
        case 401:
          message = "Authentication required - please log in";
          break;
        case 403:
          message = "Access denied - insufficient permissions";
          break;
        case 404:
          message = "Resource not found";
          break;
        case 422:
          message = "Validation failed - please check your input";
          break;
        case 500:
          message = "Server error - please try again later";
          break;
      }

      console.warn(`API Error (${status}): ${message}`);
    } else if (error.request) {
      console.error("Network error - no response received");
      store.dispatch("errors/setErrors", {
        network: "Network error - please check your connection",
      });
    } else {
      console.error("Request configuration error:", error.message);
      store.dispatch("errors/setErrors", { general: error.message });
    }

    return Promise.reject(error);
  }
);

export default axios;
